import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

// Types based on API response
export interface Campaign {
  id: string;
  accountId: string;
  restaurantId: string;
  eventId: string;
  event: {
    name: string;
    date: string;
    description: string;
    [key: string]: any;
  };
  flyer: {
    image: {
      name: string;
      original_name: string;
      storage_url: string;
      mode: string;
      properties: any;
    };
    video: any;
  };
  socialMediaId: string;
  title: string;
  type: string; // "system_generated" | "MANUAL" | "festival"
  mode: string;
  offerDetails: string;
  instructions: string;
  description: string;
  promptId: string;
  tagLine: string;
  status: string;
  flyerId: string;
  contactInfo: string;
  validFrom: string;
  validTo: string;
  themeColour: string;
  menuItems: any[];
  refImage: string[];
  combo: any[];
  isDeleted: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  campaignName: string;
  flyerUrl: string;
}

export interface CampaignsState {
  data: Campaign[];
  loading: boolean;
  error: string | null;
  deleting: string | null; // Campaign ID being deleted
  lastFetched: number | null; // Timestamp of last fetch
  isFetched: boolean; // Flag to track if data has been loaded at least once
}

const initialState: CampaignsState = {
  data: [],
  loading: false,
  error: null,
  deleting: null,
  lastFetched: null,
  isFetched: false
};

const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    fetchCampaignsRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchCampaignsSuccess: (state, action: PayloadAction<Campaign[]>) => {
      state.loading = false;
      state.data = action.payload;
      state.error = null;
      state.lastFetched = Date.now();
      state.isFetched = true;
    },
    fetchCampaignsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.isFetched = true;
    },
    // Delete Campaign Actions
    deleteCampaignRequest: (state, action: PayloadAction<string>) => {
      state.deleting = action.payload;
      state.error = null;
    },
    deleteCampaignSuccess: (state, action: PayloadAction<string>) => {
      // Remove campaign from local state immediately
      state.data = state.data.filter(campaign => campaign.id !== action.payload);
      state.deleting = null;
      state.error = null;
    },
    deleteCampaignFailure: (state, action: PayloadAction<string>) => {
      state.deleting = null;
      state.error = action.payload;
    },
    clearCampaigns: (state) => {
      state.data = [];
      state.loading = false;
      state.error = null;
      state.deleting = null;
    },
    // Reset cache to force fresh API call
    resetCampaignsCache: (state) => {
      state.isFetched = false;
      state.lastFetched = null;
    }
  }
});

export const {
  fetchCampaignsRequest,
  fetchCampaignsSuccess,
  fetchCampaignsFailure,
  deleteCampaignRequest,
  deleteCampaignSuccess,
  deleteCampaignFailure,
  clearCampaigns,
  resetCampaignsCache
} = campaignsSlice.actions;

export default campaignsSlice.reducer;
